import { api } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'
import { listQuery } from '@/lib/pagination'
import type { PrismCatalogQuery } from '@lib/input/prism-import.input'

import {
  prismImportJobDtoSchema,
  prismImportJobListDtoSchema,
  prismCatalogDtoSchema,
  prismCatalogScanDtoSchema,
  prismSessionDtoSchema,
  prismWipePreviewDtoSchema,
  prismWipeResultDtoSchema,
  type PrismImportJobDto,
  type PrismImportJobListDto,
  type PrismCatalogDto,
  type PrismCatalogScanDto,
  type PrismSessionDto,
  type PrismWipePreviewDto,
  type PrismWipeResultDto,
} from '@lib/dto/prism-import.dto'
import type {
  SavePrismSessionInput,
  StartPrismImportInput,
  WipeTenantDataInput,
} from '@lib/input/prism-import.input'

/** Temporary admin client for Social Tables import jobs. */
class PrismImportService {
  async list(): Promise<PrismImportJobListDto> {
    try {
      return await api.get('/admin/prism-import', prismImportJobListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list prism import jobs', {}, error)
      throw error
    }
  }

  async get(id: string): Promise<PrismImportJobDto> {
    try {
      return await api.get(`/admin/prism-import/${id}`, prismImportJobDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load prism import job', { id }, error)
      throw error
    }
  }

  /** What is known about the stored Social Tables session (never the session itself). */
  async session(): Promise<PrismSessionDto> {
    try {
      return await api.get('/admin/prism-import/session', prismSessionDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the prism session', {}, error)
      throw error
    }
  }

  async saveSession(input: SavePrismSessionInput): Promise<PrismSessionDto> {
    try {
      return await api.put('/admin/prism-import/session', input, prismSessionDtoSchema)
    } catch (error) {
      // Deliberately without the payload: it is a live sign-in for that account.
      logRequestFailure('Failed to store the prism session', {}, error)
      throw error
    }
  }

  async clearSession(): Promise<PrismSessionDto> {
    try {
      return await api.delFor('/admin/prism-import/session', prismSessionDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to clear the prism session', {}, error)
      throw error
    }
  }

  /** How much data a reset would clear, and what it leaves alone. */
  async wipePreview(): Promise<PrismWipePreviewDto> {
    try {
      return await api.get('/admin/prism-import/wipe', prismWipePreviewDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the reset preview', {}, error)
      throw error
    }
  }

  /** Clear the organization's imported data. Not reversible. */
  async wipe(input: WipeTenantDataInput): Promise<PrismWipeResultDto> {
    try {
      return await api.post('/admin/prism-import/wipe', input, prismWipeResultDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to reset the organization data', {}, error)
      throw error
    }
  }

  /** The kept catalog of Social Tables events, with import status per event. */
  async catalog(query: PrismCatalogQuery = {}, page?: number): Promise<PrismCatalogDto> {
    try {
      return await api.get(
        `/admin/prism-import/catalog${listQuery({ ...query }, page)}`,
        prismCatalogDtoSchema,
      )
    } catch (error) {
      logRequestFailure('Failed to load the Prism event catalog', {}, error)
      throw error
    }
  }

  /** Crawl Social Tables' upcoming events and append them to the catalog. */
  async scanCatalog(): Promise<PrismCatalogScanDto> {
    try {
      return await api.post('/admin/prism-import/catalog/scan', {}, prismCatalogScanDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to scan Social Tables for upcoming events', {}, error)
      throw error
    }
  }

  async setCatalogVerified(catalogId: string, verified: boolean): Promise<PrismCatalogDto> {
    try {
      return await api.put(
        `/admin/prism-import/catalog/${catalogId}/verified`,
        { verified },
        prismCatalogDtoSchema,
      )
    } catch (error) {
      logRequestFailure('Failed to set the verified flag on a catalog event', {}, error)
      throw error
    }
  }

  async importCatalogEvent(catalogId: string): Promise<PrismImportJobDto> {
    try {
      return await api.post(
        `/admin/prism-import/catalog/${catalogId}/import`,
        {},
        prismImportJobDtoSchema,
      )
    } catch (error) {
      logRequestFailure('Failed to import a catalog event', {}, error)
      throw error
    }
  }

  async reimportCatalogEvent(catalogId: string): Promise<PrismImportJobDto> {
    try {
      return await api.post(
        `/admin/prism-import/catalog/${catalogId}/reimport`,
        {},
        prismImportJobDtoSchema,
      )
    } catch (error) {
      logRequestFailure('Failed to re-import a catalog event', {}, error)
      throw error
    }
  }

  async start(input: StartPrismImportInput): Promise<PrismImportJobDto> {
    try {
      return await api.post('/admin/prism-import', input, prismImportJobDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to start prism import job', {}, error)
      throw error
    }
  }

  async cancel(id: string): Promise<PrismImportJobDto> {
    try {
      return await api.post(`/admin/prism-import/${id}/cancel`, {}, prismImportJobDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to cancel prism import job', { id }, error)
      throw error
    }
  }
}

export const prismImportService = new PrismImportService()
